import { useState, createContext, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useSocket } from './SocketContext';

type RoomContextType = {
  roomId: string;
  players: any;
  isRunning: boolean;
  countdown: number;
  questions: Question[];
};

type Operation = '+' | '-' | '*' | '/';

type Question = {
  firstValue: number;
  secondValue: number;
  operation: Operation;
};

type JoinRoomDto = {
  status: Status;
  message?: string;
  players?: Player[];
  questions?: Question[];
};

type MatchStartedDto = {
  questions: Question[];
};

export const RoomContext = createContext<RoomContextType | null>(null);

const RoomProvider = ({ children }: any) => {
  const { roomId } = useParams();
  const socket = useSocket();
  const navigate = useNavigate();

  const [players, setPlayers] = useState<Player[]>([]);
  const [isRunning, setIsRunning] = useState<boolean>(false);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [countdown, setCountdown] = useState<number>(4);

  useEffect(() => {
    socket.on('playerJoined', (player: Player) => {
      setPlayers((oldPlayers) => [...oldPlayers, player]);
    });

    socket.on('playerLeft', (playerId: string) => {
      setPlayers((oldPlayers) =>
        oldPlayers.filter((oldPlayer) => oldPlayer.id !== playerId),
      );
    });

    socket.on('matchStarted', (matchStartedDto: MatchStartedDto) => {
      setQuestions(matchStartedDto.questions);
      setIsRunning(true);
    });

    socket.on('matchFinished', () => {
      setIsRunning(false);
      setQuestions([]);
      setCountdown(4);
    });

    // emitted when change ready status, solve question, and so on
    socket.on('playerChanged', (player: Player) => {
      setPlayers((oldPlayers) =>
        oldPlayers.map((oldPlayer) => {
          return oldPlayer.id === player.id ? player : oldPlayer;
        }),
      );
    });

    socket.on('countdownChanged', (newCountdownValue: number) => {
      setCountdown(newCountdownValue);
    });
  }, []);

  if (!roomId) {
    toast.error('Room is undefined');
    navigate('/');
    throw new Error('Room is undefined');
  }

  useEffect(() => {
    socket.emit(
      'joinRoom',
      {
        roomId: roomId,
      },
      (response: JoinRoomDto) => {
        if (response.status !== 'ok') {
          toast.error(response.message);
          navigate('/');
        } else {
          setPlayers(response.players || []);
          setQuestions(response.questions || []);
        }
      },
    );
  }, []);

  return (
    <RoomContext.Provider
      value={{
        roomId,
        players,
        isRunning,
        countdown,
        questions,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
};

export const useRoom = () => {
  const context = useContext(RoomContext);

  if (!context) throw new Error('useUser must be used within a RoomProvider');

  return context;
};

export default RoomProvider;
